import {
  userErrorMessage,
  MAX_SOLUTION_LINE_MOVES,
  type BoardPoint,
  type BoardSnapshot,
  type HomeworkAnswerPreviewResponse,
  type ProblemMoveIntent,
} from '@wx-weiqi/contracts';

export type HomeworkResultResearch = (
  answer: readonly ProblemMoveIntent[],
) => Promise<HomeworkAnswerPreviewResponse['data']>;

interface ResearchState {
  readonly active: boolean;
  readonly pending: boolean;
  readonly moves: readonly BoardPoint[];
  readonly boards: readonly BoardSnapshot[];
  readonly cursor: number;
  readonly error: string | null;
}

/** Presentation history only. Every added move is validated by the server. */
export class HomeworkResultResearchStore {
  private state: ResearchState;
  private epoch = 0;
  private readonly listeners = new Set<() => void>();

  constructor(
    board: BoardSnapshot,
    private readonly research: HomeworkResultResearch,
  ) {
    this.state = {
      active: false,
      pending: false,
      moves: [],
      boards: [board],
      cursor: 0,
      error: null,
    };
  }

  getSnapshot = (): ResearchState => this.state;
  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  toggle(): void {
    this.epoch += 1;
    this.update(
      this.state.active
        ? {
            active: false,
            pending: false,
            moves: [],
            boards: [this.state.boards[0]!],
            cursor: 0,
            error: null,
          }
        : { ...this.state, active: true, pending: false, error: null },
    );
  }

  cancel(): void {
    this.epoch += 1;
  }

  seek(cursor: number): void {
    if (this.state.pending || !Number.isFinite(cursor)) return;
    this.update({
      ...this.state,
      cursor: Math.max(0, Math.min(this.state.moves.length, Math.trunc(cursor))),
      error: null,
    });
  }

  async place(point: BoardPoint): Promise<void> {
    const state = this.state;
    if (!state.active || state.pending) return;
    const board = state.boards[state.cursor]!;
    if (
      !Number.isInteger(point.x) ||
      !Number.isInteger(point.y) ||
      point.x < 0 ||
      point.y < 0 ||
      point.x >= board.size ||
      point.y >= board.size ||
      board.stones[point.y * board.size + point.x] !== null
    )
      return;
    if (state.cursor >= MAX_SOLUTION_LINE_MOVES) {
      this.update({
        ...state,
        error: `研究最多支持 ${MAX_SOLUTION_LINE_MOVES} 手，可后退后继续试下。`,
      });
      return;
    }
    const request = ++this.epoch;
    const moves = [...state.moves.slice(0, state.cursor), point];
    this.update({ ...state, pending: true, error: null });
    try {
      const result = await this.research(moves.map((move) => ({ kind: 'PLAY', point: move })));
      if (request !== this.epoch) return;
      this.update({
        ...state,
        moves,
        boards: [...state.boards.slice(0, state.cursor + 1), result.board],
        cursor: moves.length,
        pending: false,
        error: null,
      });
    } catch (error: unknown) {
      if (request === this.epoch)
        this.update({
          ...state,
          pending: false,
          error: userErrorMessage(error, '落子失败，请重试。'),
        });
    }
  }

  private update(state: ResearchState): void {
    this.state = state;
    this.listeners.forEach((listener) => listener());
  }
}
